import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { Code, ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey, createQueryOptions, useTransport } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import { toast } from 'sonner'

import { getEmailThread, listEmailDrafts } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  GetEmailThreadRequestSchema,
  ListEmailDraftsRequestSchema,
  type EmailDraft,
  type GetEmailThreadResponse,
} from '@/gen/cortado/v1/emails_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { captureError } from '@/lib/sentry'
import { useDiscardEmailDraft, useSaveEmailDraft } from '@/stores/emails.store'
import {
  draftSnapshotsEqual,
  emptyDraftSnapshot,
  isHtmlEmpty,
  mapDraftAttachments,
  type DraftSnapshot,
  type EmailComposeFormValues,
  type StagedAttachment,
} from './email-compose-state'

type DraftStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict'

interface BooleanRef {
  current: boolean
}

interface ApplyDraftValues {
  subject: string
  body: string
  fromAccountId?: string
}

interface UseEmailDraftSessionParams {
  open: boolean
  isReply: boolean
  organizationId: string
  parentType: ParentTypeEnum
  parentId: string
  threadId?: string
  toEmail: string
  currentUserDraft?: EmailDraft
  initialComposeDraft?: EmailDraft
  defaultAccountId: string
  defaultSubject: string
  emailSignatureHtml: string
  draftVersion: number
  applyingDraftRef: BooleanRef
  userEditedRef: BooleanRef
  toEmails: string[]
  ccEmails: string[]
  bccEmails: string[]
  attachments: StagedAttachment[]
  setToEmails: (emails: string[]) => void
  setCcEmails: (emails: string[]) => void
  setBccEmails: (emails: string[]) => void
  setCcVisible: (visible: boolean) => void
  setBccVisible: (visible: boolean) => void
  setAttachments: (attachments: StagedAttachment[]) => void
  setFromAccountId: (accountId: string) => void
  clearLibraryAttachments: () => void
  resetAttachments: () => void
  cleanupStagedAttachments: (attachments: StagedAttachment[]) => void
  setIncludeSignature: (include: boolean) => void
  getFormValues: () => EmailComposeFormValues
  applyFormValues: (values: ApplyDraftValues) => void
  getEditorHtml: () => string | undefined
  setEditorContent: (html: string) => void
  onOpenChange: (open: boolean) => void
}

export function useEmailDraftSession({
  open,
  isReply,
  organizationId,
  parentType,
  parentId,
  threadId,
  toEmail,
  currentUserDraft,
  initialComposeDraft,
  defaultAccountId,
  defaultSubject,
  emailSignatureHtml,
  draftVersion,
  applyingDraftRef,
  userEditedRef,
  toEmails,
  ccEmails,
  bccEmails,
  attachments,
  setToEmails,
  setCcEmails,
  setBccEmails,
  setCcVisible,
  setBccVisible,
  setAttachments,
  setFromAccountId,
  clearLibraryAttachments,
  resetAttachments,
  cleanupStagedAttachments,
  setIncludeSignature,
  getFormValues,
  applyFormValues,
  getEditorHtml,
  setEditorContent,
  onOpenChange,
}: UseEmailDraftSessionParams) {
  const queryClient = useQueryClient()
  const transport = useTransport()
  const { saveDraft } = useSaveEmailDraft()
  const { discardDraft, isLoading: isDiscardingDraft } = useDiscardEmailDraft()
  const [draftStatus, setDraftStatus] = React.useState<DraftStatus>('idle')
  const [hasDraftConflict, setHasDraftConflict] = React.useState(false)
  const draftTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const initializedOpenRef = React.useRef(false)
  const suppressDraftSaveRef = React.useRef(false)
  const hydratedDraftIdRef = React.useRef<string | null>(null)
  const lastSavedDraftRef = React.useRef<DraftSnapshot>(emptyDraftSnapshot)
  const draftUpdatedAtRef = React.useRef<Timestamp | undefined>(undefined)

  const clearDraftTimer = React.useCallback(() => {
    if (!draftTimerRef.current) return
    clearTimeout(draftTimerRef.current)
    draftTimerRef.current = null
  }, [])

  const applyDraft = React.useCallback(
    (params: {
      subject: string
      body: string
      draftId: string | null
      toEmails?: string[]
      ccEmails?: string[]
      bccEmails?: string[]
      fromAccountId?: string
      attachments?: StagedAttachment[]
      updatedAt?: Timestamp
    }) => {
      applyingDraftRef.current = true
      const resolvedToEmails = params.toEmails ?? (toEmail ? [toEmail] : [])
      const resolvedCcEmails = params.ccEmails ?? []
      const resolvedBccEmails = params.bccEmails ?? []
      const resolvedAttachments = params.attachments ?? []

      applyFormValues({
        subject: params.subject,
        body: params.body,
        fromAccountId: params.fromAccountId,
      })
      setToEmails(resolvedToEmails)
      setCcEmails(resolvedCcEmails)
      setBccEmails(resolvedBccEmails)
      setCcVisible(resolvedCcEmails.length > 0)
      setBccVisible(resolvedBccEmails.length > 0)
      setAttachments(resolvedAttachments)
      lastSavedDraftRef.current = {
        subject: params.subject,
        body: params.body,
        toEmails: resolvedToEmails,
        ccEmails: resolvedCcEmails,
        bccEmails: resolvedBccEmails,
        fromAccountId: params.fromAccountId ?? '',
        attachmentIds: resolvedAttachments.map((attachment) => attachment.id),
      }
      hydratedDraftIdRef.current = params.draftId
      draftUpdatedAtRef.current = params.draftId ? params.updatedAt : undefined
      setDraftStatus(params.draftId ? 'saved' : 'idle')
      setHasDraftConflict(false)
      queueMicrotask(() => {
        applyingDraftRef.current = false
      })
    },
    [
      applyFormValues,
      applyingDraftRef,
      setAttachments,
      setBccEmails,
      setBccVisible,
      setCcEmails,
      setCcVisible,
      setToEmails,
      toEmail,
    ],
  )

  React.useEffect(() => {
    if (!open) {
      initializedOpenRef.current = false
      userEditedRef.current = false
      suppressDraftSaveRef.current = false
      hydratedDraftIdRef.current = null
      draftUpdatedAtRef.current = undefined
      setDraftStatus('idle')
      setHasDraftConflict(false)
      clearDraftTimer()
      return
    }

    if (initializedOpenRef.current) return
    initializedOpenRef.current = true
    setFromAccountId(defaultAccountId)
    setIncludeSignature(Boolean(emailSignatureHtml))
    clearLibraryAttachments()

    if (threadId && currentUserDraft) {
      applyDraft({
        subject: currentUserDraft.subject || defaultSubject,
        body: currentUserDraft.body ?? '',
        draftId: currentUserDraft.id,
        ccEmails: currentUserDraft.ccEmails,
        bccEmails: currentUserDraft.bccEmails,
        attachments: mapDraftAttachments(currentUserDraft),
        updatedAt: currentUserDraft.updatedAt,
      })
    } else if (!threadId && initialComposeDraft) {
      applyDraft({
        subject: initialComposeDraft.subject || defaultSubject,
        body: initialComposeDraft.body ?? '',
        draftId: initialComposeDraft.id,
        toEmails: initialComposeDraft.toEmails.length > 0 ? initialComposeDraft.toEmails : undefined,
        ccEmails: initialComposeDraft.ccEmails,
        bccEmails: initialComposeDraft.bccEmails,
        fromAccountId: initialComposeDraft.fromAccountId,
        attachments: mapDraftAttachments(initialComposeDraft),
        updatedAt: initialComposeDraft.updatedAt,
      })
    } else {
      applyDraft({ subject: defaultSubject, body: '', draftId: null })
    }
  }, [
    applyDraft,
    clearDraftTimer,
    clearLibraryAttachments,
    currentUserDraft,
    defaultAccountId,
    defaultSubject,
    emailSignatureHtml,
    initialComposeDraft,
    open,
    setFromAccountId,
    setIncludeSignature,
    threadId,
    userEditedRef,
  ])

  React.useEffect(() => {
    if (!open || !threadId || !currentUserDraft || userEditedRef.current) return
    if (hydratedDraftIdRef.current === currentUserDraft.id) return

    applyDraft({
      subject: currentUserDraft.subject || defaultSubject,
      body: currentUserDraft.body ?? '',
      draftId: currentUserDraft.id,
      ccEmails: currentUserDraft.ccEmails,
      bccEmails: currentUserDraft.bccEmails,
      attachments: mapDraftAttachments(currentUserDraft),
      updatedAt: currentUserDraft.updatedAt,
    })
  }, [applyDraft, currentUserDraft, defaultSubject, open, threadId, userEditedRef])

  React.useEffect(() => {
    if (!open || userEditedRef.current) return
    const body = getFormValues().body
    const editorHtml = getEditorHtml()
    if (!body || editorHtml === undefined || editorHtml === body) return

    applyingDraftRef.current = true
    setEditorContent(body)
    queueMicrotask(() => {
      applyingDraftRef.current = false
    })
  }, [applyingDraftRef, getEditorHtml, getFormValues, open, setEditorContent, userEditedRef])

  const persistCurrentDraft = React.useCallback(async () => {
    if (suppressDraftSaveRef.current) return false
    if (isReply && !threadId) return false

    const { subject, body, fromAccountId } = getFormValues()
    const attachmentIds = attachments
      .filter((attachment) => !attachment.uploading && !attachment.id.startsWith('temp-'))
      .map((attachment) => attachment.id)
    const snapshot: DraftSnapshot = {
      subject,
      body,
      toEmails: isReply ? [] : toEmails,
      ccEmails,
      bccEmails,
      fromAccountId: isReply ? '' : fromAccountId,
      attachmentIds,
    }
    const hasContent = isReply
      ? Boolean(subject.trim()) ||
        !isHtmlEmpty(body) ||
        ccEmails.length > 0 ||
        bccEmails.length > 0 ||
        attachmentIds.length > 0
      : Boolean(subject.trim()) ||
        !isHtmlEmpty(body) ||
        toEmails.length > 0 ||
        ccEmails.length > 0 ||
        bccEmails.length > 0 ||
        attachmentIds.length > 0
    if (!hasContent || draftSnapshotsEqual(lastSavedDraftRef.current, snapshot)) {
      return false
    }

    setDraftStatus('saving')
    try {
      const response = await saveDraft({
        organizationId,
        threadId: isReply ? threadId : undefined,
        draftId: hydratedDraftIdRef.current ?? undefined,
        parentType: isReply ? undefined : parentType,
        parentId: isReply ? undefined : parentId,
        fromAccountId: isReply ? undefined : fromAccountId,
        toEmails: snapshot.toEmails,
        ccEmails,
        bccEmails,
        subject,
        body,
        attachmentIds,
        expectedUpdatedAt: hydratedDraftIdRef.current ? draftUpdatedAtRef.current : undefined,
      })
      lastSavedDraftRef.current = snapshot
      hydratedDraftIdRef.current = response.emailDraft?.id ?? hydratedDraftIdRef.current
      draftUpdatedAtRef.current = response.emailDraft?.updatedAt
      setDraftStatus('saved')
      return true
    } catch (error) {
      if (error instanceof ConnectError && error.code === Code.FailedPrecondition) {
        setDraftStatus('conflict')
        setHasDraftConflict(true)
        return false
      }
      setDraftStatus('error')
      reportError(error, { feature: 'email_draft_save', kind: 'mutation' })
      captureError(error instanceof Error ? error : new Error(String(error)), {
        context: 'email-compose-sheet-draft-save',
      })
      return false
    }
  }, [
    attachments,
    bccEmails,
    ccEmails,
    getFormValues,
    isReply,
    organizationId,
    parentId,
    parentType,
    saveDraft,
    threadId,
    toEmails,
  ])

  React.useEffect(() => {
    if (!open || suppressDraftSaveRef.current || !userEditedRef.current || hasDraftConflict) {
      return
    }

    clearDraftTimer()
    draftTimerRef.current = setTimeout(() => {
      void persistCurrentDraft()
      draftTimerRef.current = null
    }, 3000)

    return clearDraftTimer
  }, [clearDraftTimer, draftVersion, hasDraftConflict, open, persistCurrentDraft, userEditedRef])

  const handleOverwriteDraftConflict = React.useCallback(async () => {
    draftUpdatedAtRef.current = undefined
    const saved = await persistCurrentDraft()
    if (saved) {
      setHasDraftConflict(false)
      return
    }
    toast.error('Failed to save your draft', {
      description: 'Your changes are still here — try again.',
    })
  }, [persistCurrentDraft])

  const handleReloadDraftConflict = React.useCallback(async () => {
    try {
      if (isReply && threadId) {
        const threadDetailKey = createConnectQueryKey({
          schema: getEmailThread,
          transport,
          input: create(GetEmailThreadRequestSchema, { id: threadId }),
          cardinality: 'finite',
        })
        await queryClient.invalidateQueries({ queryKey: threadDetailKey })
        const refreshed = queryClient.getQueryData<GetEmailThreadResponse>(threadDetailKey)
        const latestDraft = refreshed?.currentUserEmailDraft
        if (latestDraft) {
          applyDraft({
            subject: latestDraft.subject || defaultSubject,
            body: latestDraft.body ?? '',
            draftId: latestDraft.id,
            ccEmails: latestDraft.ccEmails,
            bccEmails: latestDraft.bccEmails,
            attachments: mapDraftAttachments(latestDraft),
            updatedAt: latestDraft.updatedAt,
          })
        } else {
          applyDraft({ subject: defaultSubject, body: '', draftId: null })
        }
      } else {
        const latest = await queryClient.fetchQuery(
          createQueryOptions(
            listEmailDrafts,
            create(ListEmailDraftsRequestSchema, {
              organizationId,
              composesOnly: true,
              parentType,
              parentId,
            }),
            { transport },
          ),
        )
        const latestDraft = latest.drafts.find((draft) => draft.id === hydratedDraftIdRef.current)
        if (latestDraft) {
          applyDraft({
            subject: latestDraft.subject || defaultSubject,
            body: latestDraft.body ?? '',
            draftId: latestDraft.id,
            toEmails: latestDraft.toEmails,
            ccEmails: latestDraft.ccEmails,
            bccEmails: latestDraft.bccEmails,
            fromAccountId: latestDraft.fromAccountId,
            attachments: mapDraftAttachments(latestDraft),
            updatedAt: latestDraft.updatedAt,
          })
        } else {
          applyDraft({ subject: defaultSubject, body: '', draftId: null })
        }
      }
      userEditedRef.current = false
      setHasDraftConflict(false)
      toast.success('Loaded the latest saved draft')
    } catch (error) {
      reportError(error, {
        feature: 'email_draft_conflict_reload',
        kind: 'mutation',
      })
      captureError(error instanceof Error ? error : new Error(String(error)), {
        context: 'email-compose-sheet-draft-conflict-reload',
      })
      toast.error('Couldn’t load the latest draft', {
        description: getErrorDescription(error),
      })
    }
  }, [
    applyDraft,
    defaultSubject,
    isReply,
    organizationId,
    parentId,
    parentType,
    queryClient,
    threadId,
    transport,
    userEditedRef,
  ])

  const handleDiscardDraft = React.useCallback(async () => {
    if (!threadId && !hydratedDraftIdRef.current) {
      cleanupStagedAttachments(attachments)
      resetAttachments()
      applyDraft({ subject: defaultSubject, body: '', draftId: null })
      userEditedRef.current = false
      onOpenChange(false)
      return
    }

    clearDraftTimer()
    suppressDraftSaveRef.current = true
    try {
      await discardDraft({
        organizationId,
        threadId,
        draftId: threadId ? undefined : (hydratedDraftIdRef.current ?? undefined),
      })
      cleanupStagedAttachments(attachments)
      resetAttachments()
      applyDraft({ subject: defaultSubject, body: '', draftId: null })
      userEditedRef.current = false
      toast.success('Draft discarded')
      onOpenChange(false)
    } catch (error) {
      suppressDraftSaveRef.current = false
      reportError(error, { feature: 'email_draft_discard', kind: 'mutation' })
      captureError(error instanceof Error ? error : new Error(String(error)), {
        context: 'email-compose-sheet-draft-discard',
      })
      toast.error('Failed to discard draft', {
        description: getErrorDescription(error),
      })
    }
  }, [
    applyDraft,
    attachments,
    cleanupStagedAttachments,
    clearDraftTimer,
    defaultSubject,
    discardDraft,
    onOpenChange,
    organizationId,
    resetAttachments,
    threadId,
    userEditedRef,
  ])

  const beginSend = React.useCallback(() => {
    suppressDraftSaveRef.current = true
    clearDraftTimer()
    return hydratedDraftIdRef.current ?? undefined
  }, [clearDraftTimer])

  const cancelSend = React.useCallback(() => {
    suppressDraftSaveRef.current = false
  }, [])

  const completeSend = React.useCallback(() => {
    lastSavedDraftRef.current = emptyDraftSnapshot
    userEditedRef.current = false
    hydratedDraftIdRef.current = null
    draftUpdatedAtRef.current = undefined
  }, [userEditedRef])

  return {
    draftStatus,
    hasDraftConflict,
    isDiscardingDraft,
    persistCurrentDraft,
    handleOverwriteDraftConflict,
    handleReloadDraftConflict,
    handleDiscardDraft,
    beginSend,
    cancelSend,
    completeSend,
  }
}
